import {useState} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Avatar, AvatarFallback} from '@common-web/components/ui/avatar';
import {
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Upload,
  Download,
  Edit,
  Lock,
  Eye,
  AlertCircle,
  Loader2,
  MessageSquare,
  Trash2,
} from 'lucide-react';
import {RfcAction} from '@common/types/rfc';

interface RfcHistoryItem {
  id: string;
  action: RfcAction;
  description: string | null;
  oldData?: any;
  newData?: any;
  changedBy: string | null;
  changedByUser?: {
    id: string;
    name: string;
    email: string;
  } | null;
  userRole?: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export function RfcHistory({
  history,
  showInternalActions = false,
  onDownloadPdf,
}: {
  history: RfcHistoryItem[];
  showInternalActions?: boolean;
  onDownloadPdf?: (historyId: string) => Promise<void>;
}) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadPdf = async (historyId: string) => {
    if (!onDownloadPdf) return;
    setDownloadingId(historyId);
    try {
      await onDownloadPdf(historyId);
    } finally {
      setDownloadingId(null);
    }
  };

  const getActionIcon = (action: RfcAction) => {
    switch (action) {
      case RfcAction.CREATED:
        return <FileText className='h-4 w-4 text-blue-500' />;
      case RfcAction.UPDATED:
        return <Edit className='h-4 w-4 text-blue-500' />;
      case RfcAction.SUBMITTED:
        return <CheckCircle className='h-4 w-4 text-green-500' />;
      case RfcAction.APPROVED:
        return <CheckCircle className='h-4 w-4 text-green-600' />;
      case RfcAction.REJECTED:
        return <XCircle className='h-4 w-4 text-red-500' />;
      case RfcAction.PROCESSED:
        return <CheckCircle className='h-4 w-4 text-blue-600' />;
      case RfcAction.COMPLETED:
        return <CheckCircle className='h-4 w-4 text-green-700' />;
      case RfcAction.ATTACHMENT_ADDED:
        return <Upload className='h-4 w-4 text-purple-500' />;
      case RfcAction.ATTACHMENT_REMOVED:
        return <Download className='h-4 w-4 text-orange-500' />;
      case RfcAction.STATUS_CHANGED:
        return <AlertCircle className='h-4 w-4 text-yellow-500' />;
      case RfcAction.INTERNAL_UPDATED:
        return <Edit className='h-4 w-4 text-orange-600' />;
      case RfcAction.PASSWORD_RESET:
        return <Lock className='h-4 w-4 text-red-400' />;
      case RfcAction.ACCESSED:
        return <Eye className='h-4 w-4 text-gray-500' />;
      case RfcAction.PROPOSAL_SAVED:
        return <FileText className='h-4 w-4 text-green-600' />;
      case RfcAction.PDF_GENERATED:
        return <FileText className='h-4 w-4 text-indigo-500' />;
      case RfcAction.NOTE_ADDED:
        return <MessageSquare className='h-4 w-4 text-green-500' />;
      case RfcAction.NOTE_UPDATED:
        return <Edit className='h-4 w-4 text-amber-500' />;
      case RfcAction.NOTE_DELETED:
        return <Trash2 className='h-4 w-4 text-red-500' />;
      default:
        return <Clock className='h-4 w-4 text-gray-500' />;
    }
  };

  const getActionColor = (action: RfcAction) => {
    switch (action) {
      case RfcAction.CREATED:
      case RfcAction.UPDATED:
        return 'blue';
      case RfcAction.SUBMITTED:
      case RfcAction.APPROVED:
      case RfcAction.PROCESSED:
      case RfcAction.COMPLETED:
        return 'green';
      case RfcAction.REJECTED:
        return 'red';
      case RfcAction.ATTACHMENT_ADDED:
        return 'purple';
      case RfcAction.ATTACHMENT_REMOVED:
        return 'orange';
      case RfcAction.STATUS_CHANGED:
        return 'yellow';
      case RfcAction.INTERNAL_UPDATED:
        return 'orange';
      case RfcAction.PASSWORD_RESET:
        return 'red';
      case RfcAction.ACCESSED:
        return 'gray';
      case RfcAction.PROPOSAL_SAVED:
        return 'green';
      case RfcAction.PDF_GENERATED:
        return 'blue';
      case RfcAction.NOTE_ADDED:
        return 'green';
      case RfcAction.NOTE_UPDATED:
        return 'amber';
      case RfcAction.NOTE_DELETED:
        return 'red';
      default:
        return 'gray';
    }
  };

  const getRoleColor = (role: string | null) => {
    switch (role?.toLowerCase()) {
      case 'cbs':
        return 'bg-red-100 text-red-800';
      case 'client':
        return 'bg-blue-100 text-blue-800';
      case 'client_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionDescription = (action: RfcAction) => {
    switch (action) {
      case RfcAction.BROKER_APPROVED:
        return 'Broker Approved';
      case RfcAction.BROKER_REJECTED:
        return 'Broker Rejected';
      case RfcAction.MSA_SENT:
        return 'MSA Sent';
      case RfcAction.MSA_SIGNED:
        return 'MSA Signed';
      case RfcAction.MSA_VOIDED:
        return 'MSA Voided';
      case RfcAction.CREATED:
        return 'RFC Created';
      case RfcAction.UPDATED:
        return 'Form Updated';
      case RfcAction.SUBMITTED:
        return 'Submitted for Review';
      case RfcAction.APPROVED:
        return 'Approved by Client';
      case RfcAction.REJECTED:
        return 'Rejected';
      case RfcAction.PROCESSED:
        return 'Processed by CBS';
      case RfcAction.COMPLETED:
        return 'Completed';
      case RfcAction.ATTACHMENT_ADDED:
        return 'File Attached';
      case RfcAction.ATTACHMENT_REMOVED:
        return 'File Removed';
      case RfcAction.STATUS_CHANGED:
        return 'Status Changed';
      case RfcAction.INTERNAL_UPDATED:
        return 'Internal Data Updated';
      case RfcAction.PASSWORD_RESET:
        return 'Password Reset';
      case RfcAction.ACCESSED:
        return 'RFC Accessed';
      case RfcAction.PROPOSAL_SAVED:
        return 'Proposal Saved';
      case RfcAction.PDF_GENERATED:
        return 'Submission PDF Generated';
      case RfcAction.NOTE_ADDED:
        return 'Note Added';
      case RfcAction.NOTE_UPDATED:
        return 'Note Updated';
      case RfcAction.NOTE_DELETED:
        return 'Note Deleted';
      default:
        return 'Unknown Action';
    }
  };

  // Filter out internal actions if not showing them
  const filteredHistory = showInternalActions
    ? history
    : history.filter(
        (item) => item.action !== RfcAction.ACCESSED && item.action !== RfcAction.INTERNAL_UPDATED,
      );

  if (filteredHistory.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Trail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8 text-muted-foreground'>No history available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Trail ({filteredHistory.length} entries)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          {filteredHistory.map((item, index) => (
            <div
              key={item.id}
              className={`flex gap-4 p-4 rounded-lg border ${
                index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className='shrink-0'>{getActionIcon(item.action)}</div>
              <div className='grow min-w-0'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='grow'>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='font-medium text-sm'>
                        {getActionDescription(item.action)}
                      </span>
                      {item.userRole && (
                        <Badge
                          variant='outline'
                          className={`text-xs ${getRoleColor(item.userRole)}`}
                        >
                          {item.userRole.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    {item.description &&
                      item.action !== RfcAction.NOTE_UPDATED &&
                      item.action !== RfcAction.NOTE_DELETED && (
                        <p className='text-sm text-gray-600 mb-2'>{item.description}</p>
                      )}
                    {item.action === RfcAction.NOTE_UPDATED && item.oldData && item.newData && (
                      <div className='flex flex-col gap-2 text-sm mb-2'>
                        <div>
                          <span className='text-xs font-medium text-gray-500'>Before: </span>
                          <span className='text-gray-600'>
                            {(item.oldData as {content?: string}).content}
                          </span>
                        </div>
                        <div>
                          <span className='text-xs font-medium text-gray-500'>After: </span>
                          <span className='text-gray-600'>
                            {(item.newData as {content?: string}).content}
                          </span>
                        </div>
                      </div>
                    )}
                    {item.action === RfcAction.NOTE_DELETED && item.oldData && (
                      <div className='text-sm mb-2'>
                        <span className='text-xs font-medium text-gray-500'>Deleted note: </span>
                        <span className='text-gray-600'>
                          {(item.oldData as {content?: string}).content}
                        </span>
                      </div>
                    )}
                    {item.action === RfcAction.PDF_GENERATED && onDownloadPdf && (
                      <Button
                        variant='outline'
                        size='sm'
                        className='mb-2'
                        disabled={downloadingId === item.id}
                        onClick={() => handleDownloadPdf(item.id)}
                      >
                        {downloadingId === item.id ? (
                          <Loader2 className='h-3 w-3 mr-1.5 animate-spin' />
                        ) : (
                          <Download className='h-3 w-3 mr-1.5' />
                        )}
                        {downloadingId === item.id
                          ? 'Downloading...'
                          : `Download ${(item.newData as {fileName?: string} | null)?.fileName ?? 'PDF'}`}
                      </Button>
                    )}
                    <div className='flex items-center gap-2 text-xs text-gray-500'>
                      {item.changedByUser ? (
                        <>
                          <Avatar className='h-4 w-4'>
                            <AvatarFallback className='text-[8px]'>
                              {item.changedByUser.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span>{item.changedByUser.name}</span>
                          <span>({item.changedByUser.email})</span>
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                  <div className='shrink-0 text-right'>
                    <div className='text-xs text-gray-500'>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </div>
                    <div className='text-xs text-gray-400'>
                      {new Date(item.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                {showInternalActions && (item.ipAddress || item.userAgent) && (
                  <div className='mt-2 text-xs text-gray-400 space-y-1'>
                    {!!item.ipAddress && <div>IP: {item.ipAddress}</div>}
                    {!!item.userAgent && (
                      <div className='truncate'>User Agent: {item.userAgent}</div>
                    )}
                  </div>
                )}
                {showInternalActions &&
                  (item.oldData || item.newData) &&
                  item.action !== RfcAction.PDF_GENERATED && (
                    <details className='mt-2'>
                      <summary className='text-xs text-blue-600 cursor-pointer hover:text-blue-800'>
                        View Data Changes
                      </summary>
                      <div className='mt-2 p-3 bg-white rounded-sm border text-xs'>
                        {item.oldData && (
                          <div className='mb-2'>
                            <div className='font-medium text-red-600 mb-1'>Old Data:</div>
                            <pre className='whitespace-pre-wrap text-red-700 bg-red-50 p-2 rounded-sm'>
                              {JSON.stringify(item.oldData, null, 2)}
                            </pre>
                          </div>
                        )}
                        {item.newData && (
                          <div>
                            <div className='font-medium text-green-600 mb-1'>New Data:</div>
                            <pre className='whitespace-pre-wrap text-green-700 bg-green-50 p-2 rounded-sm'>
                              {JSON.stringify(item.newData, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
